// src/pages/patient/PatientInvoices.jsx
//
// /patient/invoices -- the patient's appointment invoices (read-only).
//
//   * filters are a draft until Search; Search applies all of them at once,
//     Reset restores the defaults (last 90 days, everything else "All");
//   * the default date range comes from the server's filter options;
//     ?appointment_id=... prefills that filter and clears the default dates;
//   * filters, page, page size and the open invoice live in the URL;
//   * every request is sequenced, so a slow older response never replaces a
//     newer one;
//   * counts, pages and money summaries come from the server only -- amounts
//     are never summed here and currencies are never mixed;
//   * the invoice detail is read-only; the PDF downloads through the API.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { readApiError } from "../../lib/apiErrors";
import { isUuid } from "../../lib/clientRequestId";
import { addDaysIso, browserTimezone, todayInZone } from "../../lib/displayTimezone";
import useSequencedRequest from "../../lib/useSequencedRequest";
import { patientInvoicesApi } from "../../services/patientInvoices";
import { InlineError, Pill, classNames, money } from "../../components/patient/booking/bookingUi";
import Dialog from "../../components/patient/appointments/Dialog";
import ListPagination from "../../components/patient/common/ListPagination";
import InvoiceView, { InvoiceIcon, InvoicePdfButton, PAYMENT_TONES, raiseInvoiceIssueHref } from "../../components/patient/billing/InvoiceView";
import PaymentStatusCard from "../../components/patient/billing/PaymentStatusCard";

export const DEFAULT_RANGE_DAYS = 90;
const PAGE_SIZES = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

export const FILTER_KEYS = ["date_from", "date_to", "doctor_id", "doctor_name", "invoice_number", "payment_status", "invoice_status", "appointment_id"];
const EMPTY = { date_from: "", date_to: "", doctor_id: "", doctor_name: "", invoice_number: "", payment_status: "", invoice_status: "", appointment_id: "" };

const FALLBACK_PAYMENT = [
  { value: "not_paid", label: "Unpaid" },
  { value: "paid", label: "Paid" },
  { value: "partially_refunded", label: "Partially refunded" },
  { value: "refunded", label: "Refunded" },
];
const FALLBACK_INVOICE_STATUS = [
  { value: "issued", label: "Issued" },
  { value: "cancelled", label: "Cancelled" },
];

export function localDefaultRange(timeZone, now = new Date()) {
  const to = todayInZone(timeZone, now);
  return { date_from: addDaysIso(to, -DEFAULT_RANGE_DAYS), date_to: to };
}

function positiveInt(value, fallback) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

function readUrl(params, defaults) {
  const anyFilter = FILTER_KEYS.some((key) => params.has(key));
  const filters = { ...EMPTY };
  FILTER_KEYS.forEach((key) => {
    filters[key] = params.get(key) || "";
  });
  if (!anyFilter) {
    filters.date_from = defaults.date_from;
    filters.date_to = defaults.date_to;
  }
  const size = positiveInt(params.get("page_size"), DEFAULT_PAGE_SIZE);
  return {
    filters,
    usingDefaults: !anyFilter,
    page: positiveInt(params.get("page"), 1),
    pageSize: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE,
    invoiceId: params.get("invoice_id") || "",
  };
}

export function validateInvoiceFilters(filters) {
  const errors = {};
  if (filters.date_from && filters.date_to && filters.date_to < filters.date_from) {
    errors.date_to = "The To date must be on or after the From date.";
  }
  if (filters.appointment_id && !isUuid(filters.appointment_id)) {
    errors.appointment_id = "Enter a valid appointment ID (for example 8a20643b-cc1d-47de-8f49-0f1e2d3c4b5a).";
  }
  return errors;
}

function trimFilters(filters) {
  const out = {};
  FILTER_KEYS.forEach((key) => {
    out[key] = String(filters[key] || "").trim();
  });
  return out;
}

function formatDay(value, timeZone) {
  if (!value) return "—";
  try {
    const options = { year: "numeric", month: "short", day: "numeric" };
    if (timeZone) options.timeZone = timeZone;
    return new Intl.DateTimeFormat("en-CA", options).format(new Date(value));
  } catch {
    return "—";
  }
}

function RefreshIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MoneyList({ label, entries, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4" data-testid={testId}>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      {entries && entries.length ? (
        <ul className="mt-1 space-y-0.5">
          {entries.map((entry) => (
            <li key={entry.currency} className="text-base font-semibold text-text">{money(entry.amount, entry.currency)} <span className="text-xs font-normal text-muted">{entry.currency}</span></li>
          ))}
        </ul>
      ) : (
        <div className="mt-1 text-base font-semibold text-text">—</div>
      )}
    </div>
  );
}

function InvoiceRow({ invoice, timeZone, onOpen }) {
  return (
    <li>
      <button type="button" onClick={() => onOpen(invoice.id)} data-testid="invoice-row"
        className="grid w-full grid-cols-2 gap-x-3 gap-y-1 rounded-2xl border border-border bg-white p-4 text-left text-sm shadow-sm transition hover:border-primary/40 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 md:grid-cols-[1.3fr_1fr_1.4fr_1.2fr_0.8fr_1fr_1fr] md:items-center md:rounded-none md:border-0 md:border-b md:shadow-none">
        <span className="col-span-2 min-w-0 md:col-span-1">
          <span className="block truncate font-semibold text-text">{invoice.invoice_number}</span>
          {invoice.invoice_status === "cancelled" ? <span className="text-xs font-semibold text-rose-700">{invoice.invoice_status_label || "Cancelled"}</span> : null}
        </span>
        <span className="text-muted"><span className="md:sr-only">Date: </span>{formatDay(invoice.appointment_start_at || invoice.issued_at, timeZone)}</span>
        <span className="min-w-0 truncate text-text"><span className="md:sr-only">Service: </span>{invoice.service_name || "—"}</span>
        <span className="min-w-0 truncate text-text"><span className="md:sr-only">Doctor: </span>{invoice.doctor_name || "—"}</span>
        <span className="text-muted"><span className="md:sr-only">Billed by: </span>{invoice.entity_type === "clinic" ? "Clinic" : invoice.entity_type || "—"}</span>
        <span className="font-semibold text-text md:text-right">{money(invoice.total_amount, invoice.currency)} <span className="text-xs font-normal text-muted">{invoice.currency}</span></span>
        <span className="md:text-right"><Pill tone={PAYMENT_TONES[invoice.payment_status] || "neutral"}>{invoice.payment_status_label || invoice.payment_status}</Pill></span>
      </button>
    </li>
  );
}

export default function PatientInvoices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [timeZone] = useState(() => browserTimezone() || "");
  const [localDefaults] = useState(() => localDefaultRange(timeZone));
  const [initial] = useState(() => readUrl(searchParams, localDefaults));

  const [draft, setDraft] = useState(initial.filters);
  const [applied, setApplied] = useState(initial.filters);
  const [page, setPage] = useState(initial.page);
  const [pageSize, setPageSize] = useState(initial.pageSize);
  const [openId, setOpenId] = useState(initial.invoiceId);
  const [errors, setErrors] = useState(() => validateInvoiceFilters(initial.filters));
  const [reloadKey, setReloadKey] = useState(0);
  // With default dates the first list waits for the server's default range.
  const [ready, setReady] = useState(!initial.usingDefaults);
  const usingDefaultsRef = useRef(initial.usingDefaults);
  const [serverDefaults, setServerDefaults] = useState(null);

  const list = useSequencedRequest(null);
  const options = useSequencedRequest(null);
  const detail = useSequencedRequest(null);
  const runList = list.run;
  const runOptions = options.run;
  const runDetail = detail.run;
  const appliedKey = JSON.stringify(applied);
  const appliedInvalid = Object.keys(validateInvoiceFilters(applied)).length > 0;

  // Filter options (doctors, vocabularies, default date range).
  useEffect(() => {
    runOptions(({ signal }) => patientInvoicesApi.filterOptions({ display_timezone: timeZone }, { signal })).then((result) => {
      if (result?.default_date_from && result?.default_date_to) {
        const defaults = { date_from: result.default_date_from, date_to: result.default_date_to };
        setServerDefaults(defaults);
        if (usingDefaultsRef.current) {
          setDraft((prev) => ({ ...prev, ...defaults }));
          setApplied((prev) => ({ ...prev, ...defaults }));
        }
      }
      setReady(true);
    });
  }, [runOptions, timeZone]);

  // Mirror applied filters, page, size and open invoice in the URL.
  useEffect(() => {
    const next = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
      if (applied[key]) next.set(key, applied[key]);
    });
    if (page > 1) next.set("page", String(page));
    if (pageSize !== DEFAULT_PAGE_SIZE) next.set("page_size", String(pageSize));
    if (openId) next.set("invoice_id", openId);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // searchParams is compared, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey, page, pageSize, openId]);

  const load = useCallback(() => {
    if (!ready || appliedInvalid) return;
    runList(({ signal }) =>
      patientInvoicesApi.list({ ...applied, display_timezone: timeZone, page, page_size: pageSize }, { signal })
    );
    // appliedKey captures every applied value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runList, ready, appliedKey, appliedInvalid, timeZone, page, pageSize]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  // Detail for the open invoice.
  const loadDetail = useCallback(() => {
    if (!openId) return;
    runDetail(({ signal }) => patientInvoicesApi.detail(openId, { signal }));
  }, [runDetail, openId]);
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const data = list.data;
  const items = useMemo(() => data?.items || [], [data]);

  // The requested page no longer exists (results shrank): go to the last one.
  useEffect(() => {
    if (!data || list.loading) return;
    const pages = Number(data.total_pages || 0);
    if (data.total > 0 && !items.length && pages > 0 && page > pages) setPage(pages);
  }, [data, items, page, list.loading]);

  // The date pair is validated as the patient edits; the appointment ID is
  // checked on blur and on Search (not while it is still being typed).
  function updateDraft(key, value) {
    const next = { ...draft, [key]: value };
    setDraft(next);
    setErrors((prev) => {
      const found = validateInvoiceFilters(next);
      const out = {};
      if (found.date_to) out.date_to = found.date_to;
      if (key !== "appointment_id" && prev.appointment_id && found.appointment_id) out.appointment_id = found.appointment_id;
      return out;
    });
  }

  function checkAppointmentId() {
    const found = validateInvoiceFilters(trimFilters(draft));
    setErrors((prev) => {
      const out = { ...prev };
      if (found.appointment_id) out.appointment_id = found.appointment_id;
      else delete out.appointment_id;
      return out;
    });
  }

  function onSearch(event) {
    event.preventDefault();
    const next = trimFilters(draft);
    const found = validateInvoiceFilters(next);
    setErrors(found);
    if (Object.keys(found).length) {
      const firstField = found.date_to ? "invoice-date-to" : "invoice-appointment-id";
      window.setTimeout(() => document.getElementById(firstField)?.focus(), 0);
      return;
    }
    usingDefaultsRef.current = false;
    setDraft(next);
    setApplied(next);
    setPage(1);
    setReady(true);
    setReloadKey((k) => k + 1);
  }

  function onReset() {
    const defaults = serverDefaults || localDefaults;
    const next = { ...EMPTY, ...defaults };
    usingDefaultsRef.current = true;
    setDraft(next);
    setApplied(next);
    setErrors({});
    setPage(1);
    setReloadKey((k) => k + 1);
  }

  function onRefresh() {
    if (list.loading) return;
    setReloadKey((k) => k + 1);
  }

  const opt = options.data || {};
  const doctors = opt.doctors || [];
  const paymentOptions = opt.payment_statuses?.length ? opt.payment_statuses : FALLBACK_PAYMENT;
  const invoiceStatusOptions = opt.invoice_statuses?.length ? opt.invoice_statuses : FALLBACK_INVOICE_STATUS;
  const pageSizes = Array.isArray(opt.page_sizes) && opt.page_sizes.length ? opt.page_sizes : PAGE_SIZES;
  const openInvoice = detail.data && detail.data.id === openId ? detail.data : null;
  const detailNotFound = detail.error?.response?.status === 404;
  const total = Number(data?.total || 0);

  const inputClass = "mt-1 w-full rounded-2xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30";
  const fieldBorder = (key) => (errors[key] ? "border-rose-400" : "border-border");

  return (
    <div className="mx-auto w-full max-w-6xl py-2 sm:py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">ThePulseCare billing</p>
          <h1 className="mt-1 text-2xl font-semibold text-text">My invoices</h1>
          <p className="mt-1 text-sm text-muted">View, filter, preview, and download your appointment invoices</p>
        </div>
        <button type="button" onClick={onRefresh} disabled={list.loading} aria-busy={list.loading || undefined}
          className="inline-flex items-center gap-2 rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text hover:bg-slate-50 disabled:opacity-60">
          <RefreshIcon />
          {list.loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <form onSubmit={onSearch} noValidate aria-label="Filter invoices" className="mt-5 rounded-3xl border border-border bg-white p-4 shadow-soft sm:p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="invoice-date-from" className="text-xs font-semibold text-muted">From</label>
            <input id="invoice-date-from" type="date" value={draft.date_from} onChange={(e) => updateDraft("date_from", e.target.value)} className={classNames(inputClass, "border-border")} />
          </div>
          <div>
            <label htmlFor="invoice-date-to" className="text-xs font-semibold text-muted">To</label>
            <input id="invoice-date-to" type="date" value={draft.date_to} min={draft.date_from || undefined} onChange={(e) => updateDraft("date_to", e.target.value)}
              aria-invalid={errors.date_to ? "true" : undefined} aria-describedby={errors.date_to ? "invoice-date-to-error" : undefined} className={classNames(inputClass, fieldBorder("date_to"))} />
            {errors.date_to ? <p id="invoice-date-to-error" role="alert" className="mt-1 text-xs font-semibold text-rose-700">{errors.date_to}</p> : null}
          </div>
          <div>
            <label htmlFor="invoice-doctor" className="text-xs font-semibold text-muted">Doctor</label>
            <select id="invoice-doctor" value={draft.doctor_id} onChange={(e) => updateDraft("doctor_id", e.target.value)} className={classNames(inputClass, "border-border")}>
              <option value="">All doctors</option>
              {doctors.map((doctor) => <option key={doctor.id} value={doctor.id}>{doctor.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="invoice-doctor-name" className="text-xs font-semibold text-muted">Doctor name</label>
            <input id="invoice-doctor-name" type="search" value={draft.doctor_name} maxLength={100} placeholder="Search doctor name" onChange={(e) => updateDraft("doctor_name", e.target.value)} className={classNames(inputClass, "border-border")} />
          </div>
          <div>
            <label htmlFor="invoice-number" className="text-xs font-semibold text-muted">Invoice number</label>
            <input id="invoice-number" type="text" value={draft.invoice_number} maxLength={64} placeholder="INV-..." onChange={(e) => updateDraft("invoice_number", e.target.value)} className={classNames(inputClass, "border-border")} />
          </div>
          <div>
            <label htmlFor="invoice-payment" className="text-xs font-semibold text-muted">Payment</label>
            <select id="invoice-payment" value={draft.payment_status} onChange={(e) => updateDraft("payment_status", e.target.value)} className={classNames(inputClass, "border-border")}>
              <option value="">All Payment</option>
              {paymentOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="invoice-status" className="text-xs font-semibold text-muted">Invoice status</label>
            <select id="invoice-status" value={draft.invoice_status} onChange={(e) => updateDraft("invoice_status", e.target.value)} className={classNames(inputClass, "border-border")}>
              <option value="">All Invoice Status</option>
              {invoiceStatusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="invoice-appointment-id" className="text-xs font-semibold text-muted">Appointment ID</label>
            <input id="invoice-appointment-id" type="text" value={draft.appointment_id} maxLength={36} placeholder="Appointment UUID" onChange={(e) => updateDraft("appointment_id", e.target.value)} onBlur={checkAppointmentId}
              aria-invalid={errors.appointment_id ? "true" : undefined} aria-describedby={errors.appointment_id ? "invoice-appointment-id-error" : undefined} className={classNames(inputClass, fieldBorder("appointment_id"))} />
            {errors.appointment_id ? <p id="invoice-appointment-id-error" role="alert" className="mt-1 text-xs font-semibold text-rose-700">{errors.appointment_id}</p> : null}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted">{timeZone ? `Dates are in ${timeZone}.` : null}</p>
          <div className="flex gap-2">
            <button type="button" onClick={onReset} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text hover:bg-slate-50">Reset</button>
            <button type="submit" className="btn-primary-gradient rounded-2xl px-5 py-2 text-sm font-semibold text-white">Search</button>
          </div>
        </div>
      </form>

      {data?.summary ? (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="invoice-summary">
          <div className="rounded-2xl border border-border bg-white p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted">Invoices</div>
            <div className="mt-1 text-base font-semibold text-text">{data.summary.count ?? total}</div>
          </div>
          <MoneyList label="Total billed" entries={data.summary.totals_by_currency} testId="invoice-summary-totals" />
          <MoneyList label="Amount due" entries={data.summary.amount_due_by_currency} testId="invoice-summary-due" />
        </div>
      ) : null}

      <section aria-busy={list.loading || undefined} className="mt-4 rounded-3xl border border-border bg-white p-4 shadow-soft sm:p-5">
        <div className="flex items-center justify-between text-sm" aria-live="polite">
          <span className="font-semibold text-text" data-testid="invoice-count">{data ? `${total} ${total === 1 ? "invoice" : "invoices"} found` : ""}</span>
          {list.loading && data ? <span className="text-muted">Refreshing…</span> : null}
        </div>

        {list.error ? <div className="mt-3"><InlineError message={readApiError(list.error, "Invoices could not be loaded.")} onRetry={onRefresh} /></div> : null}

        {(list.loading || !ready) && !data && !list.error ? (
          <ul className="mt-3 space-y-2" aria-label="Loading invoices">
            {[0, 1, 2].map((n) => <li key={n} className="h-16 animate-pulse rounded-2xl border border-border bg-slate-50" />)}
          </ul>
        ) : null}

        {data && !items.length && !(total > 0) ? (
          <div className="mt-3 flex flex-col items-center rounded-3xl border border-dashed border-border p-8 text-center" data-testid="invoices-empty">
            <span className="rounded-2xl bg-primary/10 p-3 text-primary"><InvoiceIcon /></span>
            <p className="mt-3 text-sm font-semibold text-text">No invoices found</p>
            <p className="mt-1 text-sm text-muted">Try changing the filters or date range</p>
          </div>
        ) : null}

        {items.length ? (
          <>
            <div className="mt-3 hidden grid-cols-[1.3fr_1fr_1.4fr_1.2fr_0.8fr_1fr_1fr] gap-x-3 border-b border-border px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-muted md:grid" aria-hidden="true">
              <span>Invoice</span><span>Date</span><span>Service</span><span>Doctor</span><span>Billed by</span><span className="text-right">Amount</span><span className="text-right">Payment</span>
            </div>
            <ul className={classNames("mt-3 space-y-2 md:mt-0 md:space-y-0", list.loading && "opacity-80")} aria-label="Invoices">
              {items.map((invoice) => <InvoiceRow key={invoice.id} invoice={invoice} timeZone={timeZone} onOpen={setOpenId} />)}
            </ul>
          </>
        ) : null}

        {data && total > 0 ? (
          <ListPagination idPrefix="invoices" page={data.page || page} totalPages={data.total_pages} total={total} noun="total" pageSize={pageSize} pageSizes={pageSizes}
            disabled={list.loading} onPage={(p) => setPage(p)} onPageSize={(size) => { setPageSize(size); setPage(1); }} />
        ) : null}
      </section>

      <Dialog open={Boolean(openId)} title={openInvoice ? `Invoice ${openInvoice.invoice_number}` : "Invoice"} labelId="invoice-detail-title" onClose={() => setOpenId("")} wide>
        {openInvoice ? (
          <div>
            <InvoiceView invoice={openInvoice} timeZone={timeZone} />
            {openInvoice.payment ? (
              <div className="mt-4">
                {/* Read-only. Online payment is started from the appointment page only. */}
                <PaymentStatusCard payment={openInvoice.payment} timeZone={timeZone} />
                {openInvoice.payment.allowed_actions?.can_retry_online_payment && openInvoice.appointment_id ? (
                  <Link to={`/patient/appointments/${encodeURIComponent(openInvoice.appointment_id)}#billing`}
                    className="mt-2 inline-flex rounded-2xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white" data-testid="invoice-pay-online">
                    Pay online on the appointment page
                  </Link>
                ) : null}
              </div>
            ) : null}
            <div className="mt-4 flex flex-wrap items-start gap-2">
              <InvoicePdfButton invoice={openInvoice} download={patientInvoicesApi.downloadPdf}
                label={openInvoice.payment?.allowed_actions?.can_download_receipt ? "Download receipt (PDF)" : "Download PDF"} />
              <Link to={raiseInvoiceIssueHref(openInvoice)} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text hover:bg-slate-50">Raise invoice issue</Link>
            </div>
          </div>
        ) : detail.error ? (
          detailNotFound ? (
            <p className="text-sm text-muted" data-testid="invoice-not-found">Invoice not found. It may have been removed, or the link is incorrect.</p>
          ) : (
            <InlineError message={readApiError(detail.error, "The invoice could not be loaded.")} onRetry={loadDetail} />
          )
        ) : (
          <p className="text-sm text-muted" role="status">Loading invoice…</p>
        )}
      </Dialog>
    </div>
  );
}
